import type { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "baback-notes-read";
const MAX_ENTRIES = 500;

type ReadEntry = { id: string; noteId: string };

function readStore(): ReadEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as ReadEntry[];
  } catch {
    return [];
  }
}

function readLastSeenNoteId(returnRequestId: string): string | null {
  return readStore().find((e) => e.id === returnRequestId)?.noteId ?? null;
}

function writeLastSeenNoteId(returnRequestId: string, noteId: string): void {
  try {
    const entries = readStore().filter((e) => e.id !== returnRequestId);
    entries.unshift({ id: returnRequestId, noteId });
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(0, MAX_ENTRIES)),
    );
  } catch {
    // localStorage may be unavailable (private browsing, storage quota, etc.)
  }
}

export function useUnreadNotes(
  returnRequestId: string,
  logs: ReturnRequestLogDto[],
): { unreadCount: number; markAsRead: () => void } {
  const commentLogs = useMemo(
    () =>
      [...logs]
        .filter((l) => l.type === "comment")
        .sort((a, b) => b.date - a.date),
    [logs],
  );

  const [lastSeenNoteId, setLastSeenNoteId] = useState<string | null>(() =>
    readLastSeenNoteId(returnRequestId),
  );

  useEffect(() => {
    setLastSeenNoteId(readLastSeenNoteId(returnRequestId));
  }, [returnRequestId]);

  const markAsRead = useCallback(() => {
    const mostRecent = commentLogs[0];
    if (!mostRecent) return;
    writeLastSeenNoteId(returnRequestId, mostRecent.id);
    setLastSeenNoteId(mostRecent.id);
  }, [commentLogs, returnRequestId]);

  const unreadCount = useMemo(() => {
    if (commentLogs.length === 0) return 0;
    // First visit (nothing stored) → all notes are unread
    if (!lastSeenNoteId) return commentLogs.length;
    const idx = commentLogs.findIndex((l) => l.id === lastSeenNoteId);
    // idx === 0 → already up to date
    if (idx === 0) return 0;
    // idx === -1 → anchor note deleted/archived → treat everything as unread
    if (idx === -1) return commentLogs.length;
    return idx;
  }, [commentLogs, lastSeenNoteId]);

  return { unreadCount, markAsRead };
}
